/**
 * Super-admin drill-down profile for an empanelled lab.
 *
 * Sections: identity + accreditations, plan, ratings, business (marketplace / follow-ups / LIS),
 *           categorised users (admins · doctors · patients).
 */
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Loader2, Star, FileText, IndianRupee, Users as UsersIcon,
  Stethoscope, ShieldCheck, Award, Calendar, Layers, Search,
} from "lucide-react";

const MODE_STYLE = {
  public: { label: "Public", cls: "bg-efgh-green/10 text-efgh-green" },
  hidden: { label: "Hidden", cls: "bg-efgh-orange/10 text-efgh-orange" },
  private: { label: "Private", cls: "bg-danger/10 text-danger" },
};

export default function AdminLabProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/labs/${id}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e, "Could not load lab profile")))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <DashboardLayout role="super_admin"><div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout role="super_admin"><div className="p-10 text-center text-ink/50">Lab not found.</div></DashboardLayout>;

  const lab = data.lab;
  const eff = MODE_STYLE[data.visibility_effective] || MODE_STYLE.public;
  const biz = data.business;

  return (
    <DashboardLayout role="super_admin">
      <div className="max-w-6xl">
        <Link to="/admin/labs" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to labs</Link>

        {/* Identity */}
        <Card className="mt-4 p-6 rounded-2xl">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {lab.branding?.logo_url ? (
                <img src={lab.branding.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-border" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-efgh-orange/10 text-efgh-orange grid place-items-center"><Building2 className="w-7 h-7" /></div>
              )}
              <div>
                <h1 className="font-heading font-bold text-2xl text-ink" data-testid="lab-profile-name">{lab.name}</h1>
                <div className="text-sm text-ink/60">{lab.email || "—"} · {lab.phone || "no phone"}</div>
                <div className="text-xs text-ink/50 mt-1">{[lab.address, lab.city, lab.state, lab.pincode].filter(Boolean).join(" · ") || "No address"}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-bold ${eff.cls}`}>Effective: {eff.label}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-muted text-ink/70">Lifecycle: {lab.lifecycle_status || "active"}</span>
                  <span className="text-[10px] font-mono text-ink/50">/{lab.slug}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Accreditations */}
          {(lab.accreditation_logos || []).length > 0 && (
            <div className="mt-5 pt-5 border-t border-border/70">
              <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Accreditations</div>
              <div className="mt-2 flex flex-wrap gap-3">
                {(lab.accreditation_logos || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1">
                    {a.logo_url && <img src={a.logo_url} alt="" className="w-6 h-6 object-contain" />}
                    <span className="text-xs font-semibold text-ink">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* KPIs */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Star}        color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`${data.ratings.avg}/5`} label={`${data.ratings.count} reviews`} testid="lp-kpi-rating" />
          <Kpi icon={FileText}    color="text-efgh-blue"   bg="bg-efgh-blue/10"   value={biz.total_reports} label="Total reports" to={`/admin/reports?lab=${id}`} testid="lp-kpi-reports" />
          <Kpi icon={Calendar}    color="text-efgh-green"  bg="bg-efgh-green/10"  value={biz.total_bookings} label="Total bookings" to={`/admin/bookings?lab=${id}`} testid="lp-kpi-bookings" />
          <Kpi icon={IndianRupee} color="text-efgh-orange" bg="bg-efgh-orange/10" value={`₹${biz.revenue.toLocaleString()}`} label="Revenue (booked)" to={`/admin/billing?lab=${id}`} testid="lp-kpi-revenue" />
        </div>

        {/* Business breakdown */}
        <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-business">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-efgh-blue" />
            <div className="font-heading font-semibold text-ink">Business through EFGH</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <BizStat label="Marketplace bookings → reports" value={biz.marketplace_reports} colorClass="text-efgh-orange" />
            <BizStat label="Smart-report follow-ups" value={biz.follow_up_reports} colorClass="text-efgh-blue" />
            <BizStat label="LIS-ingested" value={biz.lis_reports} colorClass="text-efgh-green" />
          </div>
        </Card>

        {/* Plan */}
        <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-plan">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-efgh-orange" />
            <div className="font-heading font-semibold text-ink">Subscription plan</div>
          </div>
          <div className="text-sm">
            <span className="font-semibold text-ink">{data.plan.id ? data.plan.id.toUpperCase() : "No active plan"}</span>
            <span className="text-ink/60"> · status <b>{data.plan.status}</b></span>
            {data.plan.expires_at && <span className="text-ink/60"> · expires {String(data.plan.expires_at).slice(0, 10)}</span>}
          </div>
        </Card>

        {/* Commission overrides — per-lab % that beats the platform default */}
        <LabCommissionOverrides labId={id} />

        {/* Lab Wallet — admin visibility + manual adjustment */}
        <AdminLabWalletCard labId={id} />

        {/* Users — admins + doctors (patients handled separately below to support pagination) */}
        <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-users">
          <div className="flex items-center gap-2 mb-4">
            <UsersIcon className="w-4 h-4 text-efgh-blue" />
            <div className="font-heading font-semibold text-ink">Lab team</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <UserGroup title="Lab admins" icon={UsersIcon} rows={data.users.admins} render={(u) => (
              <Link to={`/admin/users/${u.id}`} className="block hover:opacity-80">
                <div className="font-semibold text-ink text-sm underline underline-offset-2 decoration-ink/20">{u.full_name || "—"}</div>
                <div className="text-[11px] text-ink/50">{u.email}</div>
              </Link>
            )} />
            <UserGroup title="Doctors on letterhead" icon={Stethoscope} rows={data.users.doctors} render={(d) => (
              <>
                <div className="font-semibold text-ink text-sm">Dr. {d.name}</div>
                <div className="text-[11px] text-ink/50">{d.qualification || ""}{d.registration_no ? ` · Reg ${d.registration_no}` : ""}</div>
              </>
            )} />
          </div>
        </Card>

        {/* Patients — paginated + searchable (scales to thousands) */}
        <LabPatientsSection labId={id} totalHint={data.users.patient_count} />
      </div>
    </DashboardLayout>
  );
}

function AdminLabWalletCard({ labId }) {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(() => {
    api.get(`/admin/labs/${labId}/wallet`).then((r) => setData(r.data)).catch(() => {});
  }, [labId]);
  useEffect(() => { load(); }, [load]);

  const adjust = async () => {
    const amt = parseFloat(amount);
    if (!amt) { toast.error("Enter a non-zero amount"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/labs/${labId}/wallet/adjust`, { amount: amt, note: note || "Admin adjustment" });
      setAmount(""); setNote("");
      toast.success(`${amt > 0 ? "Credited" : "Debited"} ₹${Math.abs(amt)}`);
      load();
    } catch (e) { toast.error(apiError(e, "Adjust failed")); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const bal = Number(data.balance_rupees || 0);
  return (
    <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-wallet-admin-card">
      <div className="flex items-center gap-2 mb-3">
        <IndianRupee className="w-4 h-4 text-efgh-green" />
        <div className="font-heading font-semibold text-ink">Lab wallet</div>
      </div>
      <div className="flex items-baseline gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink/40">Current balance</div>
          <div className="font-heading font-bold text-2xl text-ink" data-testid="lab-wallet-admin-balance">₹{bal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
        <div className={`text-xs px-2 py-1 rounded-full ${bal < 0 ? "bg-danger/10 text-danger" : bal < 500 ? "bg-amber-100 text-amber-700" : "bg-efgh-green/10 text-efgh-green"}`}>
          {bal < 0 ? "Overdrawn" : bal < 500 ? "Low balance" : "Healthy"}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-[130px_1fr_auto] gap-2 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-1">Adjust (₹)</div>
          <Input type="number" step="100" placeholder="±amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="lab-wallet-adjust-amount" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-1">Reason</div>
          <Input placeholder="Onboarding gift / correction / …" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lab-wallet-adjust-note" />
        </div>
        <Button onClick={adjust} disabled={busy} data-testid="lab-wallet-adjust-save" className="bg-ink hover:bg-ink/90 text-white h-10">{busy ? "Saving…" : "Apply"}</Button>
      </div>

      {data.transactions?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2">Recent transactions</div>
          <div className="space-y-1 max-h-56 overflow-auto">
            {data.transactions.slice(0, 10).map((t) => (
              <div key={t.id} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                <div>
                  <span className="font-mono text-[10px] text-ink/40">{String(t.created_at || "").slice(5, 16).replace("T", " ")}</span>
                  <span className={`ml-2 font-semibold ${t.direction === "credit" ? "text-efgh-green" : "text-danger"}`}>{t.direction === "credit" ? "+" : "−"}₹{t.amount}</span>
                  <span className="ml-2 text-ink/70">{t.kind}</span>
                  {t.note && <span className="ml-2 text-ink/50">— {t.note}</span>}
                </div>
                <span className="text-ink/50">bal ₹{t.balance_after}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function LabCommissionOverrides({ labId }) {  const [platformDefaults, setPlatformDefaults] = useState({ marketplace: 15, followup: 10 });
  const [labOverrides, setLabOverrides] = useState({ marketplace: "", followup: "" });
  const [initial, setInitial] = useState({ marketplace: "", followup: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/platform-settings").then((r) => {
      const c = r.data?.pricing?.commissions || {};
      setPlatformDefaults({
        marketplace: c.marketplace_commission_percent ?? 15,
        followup: c.followup_commission_percent ?? 10,
      });
    }).catch(() => {});
    api.get(`/admin/labs`).then((r) => {
      const lab = (r.data || []).find((l) => l.id === labId) || {};
      const m = lab.marketplace_commission_percent;
      const f = lab.followup_commission_percent;
      const state = { marketplace: m ?? "", followup: f ?? "" };
      setLabOverrides(state);
      setInitial(state);
    }).catch(() => {});
  }, [labId]);

  const dirty = String(labOverrides.marketplace) !== String(initial.marketplace) || String(labOverrides.followup) !== String(initial.followup);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        marketplace_commission_percent: labOverrides.marketplace === "" ? null : parseFloat(labOverrides.marketplace),
        followup_commission_percent: labOverrides.followup === "" ? null : parseFloat(labOverrides.followup),
      };
      await api.patch(`/admin/labs/${labId}`, patch);
      setInitial(labOverrides);
      toast.success("Commission overrides saved");
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const effMarketplace = labOverrides.marketplace === "" ? platformDefaults.marketplace : Number(labOverrides.marketplace);
  const effFollowup = labOverrides.followup === "" ? platformDefaults.followup : Number(labOverrides.followup);

  return (
    <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-commissions">
      <div className="flex items-center gap-2 mb-3">
        <IndianRupee className="w-4 h-4 text-efgh-yellow" />
        <div className="font-heading font-semibold text-ink">Commission overrides</div>
        <span className="text-[11px] text-ink/50">Leave blank to use platform defaults</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Marketplace bookings</div>
          <div className="mt-1 flex items-baseline gap-2">
            <Input
              type="number" step="0.5" min="0" max="100"
              placeholder={String(platformDefaults.marketplace)}
              value={labOverrides.marketplace}
              onChange={(e) => setLabOverrides((s) => ({ ...s, marketplace: e.target.value }))}
              data-testid="lab-comm-marketplace"
              className="max-w-[110px]"
            />
            <span className="text-sm text-ink/60">% · platform default {platformDefaults.marketplace}%</span>
          </div>
          <div className="mt-1 text-xs"><span className="text-ink/50">Effective:</span> <b className="text-ink">{effMarketplace}%</b> {labOverrides.marketplace === "" ? <span className="text-ink/40">(inherit)</span> : <span className="text-efgh-orange">(override)</span>}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Follow-up (Book-Now) bookings</div>
          <div className="mt-1 flex items-baseline gap-2">
            <Input
              type="number" step="0.5" min="0" max="100"
              placeholder={String(platformDefaults.followup)}
              value={labOverrides.followup}
              onChange={(e) => setLabOverrides((s) => ({ ...s, followup: e.target.value }))}
              data-testid="lab-comm-followup"
              className="max-w-[110px]"
            />
            <span className="text-sm text-ink/60">% · platform default {platformDefaults.followup}%</span>
          </div>
          <div className="mt-1 text-xs"><span className="text-ink/50">Effective:</span> <b className="text-ink">{effFollowup}%</b> {labOverrides.followup === "" ? <span className="text-ink/40">(inherit)</span> : <span className="text-efgh-orange">(override)</span>}</div>
        </div>
      </div>
      {dirty && (
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setLabOverrides(initial)} data-testid="lab-comm-reset">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="lab-comm-save" className="bg-ink hover:bg-ink/90 text-white">{saving ? "Saving…" : "Save overrides"}</Button>
        </div>
      )}
    </Card>
  );
}

function LabPatientsSection({ labId, totalHint }) {
  const PAGE = 25;
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(totalHint || 0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const load = async (o = 0, term = searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(o) });
      if (term) params.set("q", term);
      const r = await api.get(`/admin/labs/${labId}/patients?${params.toString()}`);
      setRows(r.data.patients || []);
      setTotal(r.data.total || 0);
      setOffset(o);
    } catch (err) { toast.error(apiError(err, "Could not load patients")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(0, ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [labId]);

  const submitSearch = () => { setSearchTerm(q); load(0, q); };

  const pageStart = offset + 1;
  const pageEnd = offset + rows.length;
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;

  return (
    <Card className="mt-4 p-5 rounded-2xl" data-testid="lab-patients">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-efgh-blue" />
          <div className="font-heading font-semibold text-ink">Patients <span className="text-ink/50 font-normal">({total.toLocaleString()})</span></div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
            <Input
              data-testid="lab-patients-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
              placeholder="Search name / email / phone…"
              className="pl-8 h-9 w-64"
            />
          </div>
          <Button size="sm" onClick={submitSearch} data-testid="lab-patients-search-btn">Search</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-ink/40 mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-ink/50 text-sm">No patients match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-ink/50 font-heading">
              <tr>
                <th className="text-left px-2 py-1">Patient</th>
                <th className="text-left px-2 py-1">Contact</th>
                <th className="text-right px-2 py-1">Reports at this lab</th>
                <th className="text-left px-2 py-1">Joined</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`lab-patient-row-${p.id}`}>
                  <td className="px-2 py-2">
                    <div className="font-semibold text-ink text-sm">{p.full_name || "—"}</div>
                  </td>
                  <td className="px-2 py-2 text-[11px] text-ink/60">{p.email}{p.phone ? ` · ${p.phone}` : ""}</td>
                  <td className="px-2 py-2 text-right font-mono">{p.reports_at_lab || 0}</td>
                  <td className="px-2 py-2 text-[11px] text-ink/60">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-2 text-right">
                    <Link to={`/admin/users/${p.id}`}><Button size="sm" variant="outline" data-testid={`open-patient-${p.id}`}>Open</Button></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="mt-3 flex items-center justify-between text-xs text-ink/60">
          <div>Showing {pageStart}–{pageEnd} of {total.toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={!hasPrev} onClick={() => load(Math.max(0, offset - PAGE))} data-testid="patients-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => load(offset + PAGE)} data-testid="patients-next">Next</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Kpi({ icon: Icon, color, bg, value, label, to, testid }) {
  const inner = (
    <Card className={`rounded-2xl p-4 ${to ? "card-hover cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`} data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-4 h-4" /></div>
      <div className="mt-2 font-heading font-bold text-xl text-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
function BizStat({ label, value, colorClass }) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <div className={`text-2xl font-heading font-bold ${colorClass}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-ink/60 mt-1">{label}</div>
    </div>
  );
}
function UserGroup({ title, icon: Icon, rows, render }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2">
        <Icon className="w-3 h-3" /> {title}
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="rounded-lg border border-dashed border-border p-3 text-xs text-ink/50 text-center">None yet</div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-border px-3 py-2">{render(r)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
